// Pure draft logic: one reducer for every edit, one planner for SAVE_SUBREGION.
import { CARRIER_MAPPING_COLUMNS } from '../constants';
import {
    applyCarrierEdits,
    carrierKey,
    diffDraftZips,
    EMPTY_CARRIER_EDITS,
    reconcileDraft,
    sameZipSet,
    settingsChanges,
    toggleZip,
} from './utils';

const savedZipsOf = (subregion) => (subregion?.zips || []).map((mapping) => mapping.zip);

// `editing`: 'new', a subregion_id or null. `base`: the saved snapshot reconcileDraft diffs against.
export const seedDraft = (subregion, editing) => {
    const name = subregion?.name || '';
    const zips = savedZipsOf(subregion);
    return { editing, name, zips, base: { name, zips }, carrierEdits: EMPTY_CARRIER_EDITS, settings: {} };
};

// Whether saved state moved under an open draft (the Assign popover, the list's inline rename).
export const needsReconcile = (draft, subregion) =>
    draft.base.name !== (subregion?.name || '') || !sameZipSet(draft.base.zips, savedZipsOf(subregion));

let draftKeySeq = 0;
// Unique per page load, which is all a draft row's key has to be.
export const newDraftKey = () => `draft-${++draftKeySeq}`;

// Forces a reseed on the next render.
const STALE = '__stale__';

export const draftReducer = (draft, action) => {
    switch (action.type) {
        // After a save: the refetch already has the edits.
        case 'invalidate':
            return { ...draft, editing: STALE };
        case 'reseed':
            return seedDraft(action.subregion, action.editing);
        case 'reconcile':
            return reconcileDraft(draft, { name: action.subregion?.name || '', zips: savedZipsOf(action.subregion) });
        case 'setName':
            return { ...draft, name: action.name };
        case 'toggleZip':
            return { ...draft, zips: toggleZip(draft.zips, action.zip) };
        case 'addZips': {
            // A Set, since pastes can be large. Also dedupes within the batch.
            const held = new Set(draft.zips);
            const fresh = action.zips.filter((zip) => !held.has(zip) && held.add(zip));
            return { ...draft, zips: [...draft.zips, ...fresh] };
        }
        case 'removeZip':
            return { ...draft, zips: draft.zips.filter((zip) => zip !== action.zip) };
        // `saved`: the subregion's saved carriers. The reducer applies its own edits to them, so
        // several actions before a re-render each see the ones before.
        case 'addCarrier': {
            const { carrier, saved, draftKey } = action;
            // A subregion's first carrier is its primary. After that, only "Make primary" sets one.
            const isPrimary = applyCarrierEdits(saved || [], draft.carrierEdits).length === 0;
            // Minted by the caller: an unsaved row's identity until it has a mapping_id.
            const added = {
                draft_key: draftKey,
                carrier_id: carrier.client_id,
                warehouse_id: null,
                // Column default; sent because carrierRow sends every column.
                is_direct: false,
                // NULL, not false: UNIQUE (subregion_id, is_primary) allows one false per subregion.
                is_primary: isPrimary ? true : null,
            };
            return { ...draft, carrierEdits: { ...draft.carrierEdits, added: [...draft.carrierEdits.added, added] } };
        }
        // Removing the primary makes the next carrier in list order primary: the oldest saved one, then drafted ones.
        case 'removeCarrier': {
            const { mapping, saved } = action;
            const edits = draft.carrierEdits;
            const removed = {
                ...draft,
                carrierEdits: mapping.draft_key
                    ? { ...edits, added: edits.added.filter((m) => m.draft_key !== mapping.draft_key) }
                    : { ...edits, removed: [...edits.removed, mapping.mapping_id] },
            };
            if (!mapping.is_primary) return removed;

            const [next] = applyCarrierEdits(saved || [], removed.carrierEdits);
            return next ? draftReducer(removed, { type: 'setPrimary', saved, mapping: next }) : removed;
        }
        // `mapping` becomes the primary and the old primary is cleared, as edits to each changed row.
        case 'setPrimary': {
            const { saved, mapping } = action;
            const key = carrierKey(mapping);
            return applyCarrierEdits(saved || [], draft.carrierEdits).reduce((acc, m) => {
                const is_primary = carrierKey(m) === key ? true : null;
                return (m.is_primary ?? null) === is_primary
                    ? acc
                    : draftReducer(acc, { type: 'updateCarrier', mapping: { ...m, is_primary } });
            }, draft);
        }
        case 'updateCarrier': {
            const { mapping } = action;
            const edits = draft.carrierEdits;
            return {
                ...draft,
                carrierEdits: mapping.draft_key
                    ? {
                          ...edits,
                          added: edits.added.map((m) => (m.draft_key === mapping.draft_key ? { ...m, ...mapping } : m)),
                      }
                    : { ...edits, changed: { ...edits.changed, [mapping.mapping_id]: mapping } },
            };
        }
        case 'setSetting':
            return { ...draft, settings: { ...draft.settings, [action.key]: action.value } };
        default:
            return draft;
    }
};

export const draftView = (subregion, draft) => ({
    name: draft.name,
    zips: draft.zips,
    carriers: applyCarrierEdits(subregion?.carriers || [], draft.carrierEdits),
    settings: { ...(subregion || {}), ...draft.settings },
});

// Every column, null when unset: on_conflict resets an omitted column to its default.
const carrierRow = (mapping) => Object.fromEntries(CARRIER_MAPPING_COLUMNS.map((col) => [col, mapping[col] ?? null]));

// Null when nothing changed. `set` always has `name`, so _set is never empty.
export const planSubregionSave = (subregion, draft) => {
    const subregion_id = subregion.subregion_id;
    const savedName = subregion.name || '';
    const name = draft.name.trim();

    const zips = diffDraftZips(subregion.zips || [], draft.zips);
    const settings = settingsChanges(subregion, draft.settings);

    const saved = subregion.carriers || [];
    const edits = draft.carrierEdits;
    const removed = new Set(edits.removed);
    const savedById = new Map(saved.map((m) => [m.mapping_id, m]));
    const carriers = applyCarrierEdits(saved, edits);
    const updated = carriers
        .filter((m) => savedById.has(m.mapping_id))
        // A field edited back to its saved value is not a change.
        .filter((after) => {
            const before = savedById.get(after.mapping_id);
            return CARRIER_MAPPING_COLUMNS.some((col) => (before[col] ?? null) !== (after[col] ?? null));
        });

    const variables = {
        subregion_id,
        set: { name: name || null, ...settings },
        zips: zips.toAdd.map((zip) => ({ subregion_id, zip })),
        zip_ids: zips.toRemove.map((m) => m.mapping_id),
        carrier_inserts: carriers.filter((m) => !m.mapping_id).map((m) => ({ subregion_id, ...carrierRow(m) })),
        carrier_updates: updated.map((after) => ({
            mapping_id: after.mapping_id,
            subregion_id,
            ...carrierRow(after),
        })),
        carrier_ids: saved.filter((m) => removed.has(m.mapping_id)).map((m) => m.mapping_id),
    };

    const changed =
        name !== savedName ||
        Object.keys(settings).length ||
        variables.zips.length ||
        variables.zip_ids.length ||
        variables.carrier_inserts.length ||
        variables.carrier_updates.length ||
        variables.carrier_ids.length;

    return changed ? variables : null;
};

// Carriers need a saved subregion, so a create has none.
export const planSubregionCreate = (draft) => ({
    name: draft.name.trim() || null,
    ...settingsChanges(null, draft.settings),
    zips: { data: draft.zips.map((zip) => ({ zip })) },
});

export const isDraftDirty = (subregion, draft, isNew) =>
    isNew ? !!(draft.name.trim() || draft.zips.length) : !!subregion && !!planSubregionSave(subregion, draft);
